import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { commonApiRequests } from 'utils/api-requests/common';
import { tenderOffersApiRequests } from 'utils/api-requests/tender';
import { isTouchDevice } from 'utils/checkIsTouchDevice';
import { generateSingleOfferLink } from 'utils/generateSingleOfferLink';
import { UserReactions } from 'utils/types/OfferData';
import { ReactionType } from 'utils/types/ReactionType';
import { TenderDetailsResponse } from 'utils/types/TenderModels';

import Indicator from 'components/atom/Indicator';
import OfferActions from 'components/common/Card/common/OfferActions/OfferActions';
import {
    InteractiveElements
} from 'components/common/Card/common/V2InteractiveIconsWrapper/V2InteractiveIconsWrapper';
import { IconEnum } from 'components/common/Icon';
import SingleOfferDetailsRenderer, {
    SingleOfferDetailsRendererProps
} from 'components/common/SingleOfferDetailsRenderer/SingleOfferDetailsRenderer';
import { SliderImages } from 'components/common/Slider/Slider';
import { ExtendedNote } from 'components/common/Tabs/tabs/V2NoteTab';
import { TabType } from 'components/common/Tabs/V2Tabs';
import { convertToThumbnailPath, createImagesForSlider } from 'components/functions/imagesFunctions';
import {
    tenderOfferListFunctions
} from 'components/functions/offerListFunctions/tenderOfferListFunctions/tenderOfferListFunctions';
import { generateOfferActionsElements } from 'components/functions/shared/generateOfferActionsElements';

const Wrapper = styled.div`
    margin: 15px;
`;

const NoAdvertContainer = styled.h1`
    text-align: center;
    margin-top: 15%;
`;

const TENDER_MODULE = 'TENDER';

const SingleTenderDetailPage = () => {
    const { hash } = useParams();
    const [preparedAdvert, setPreparedAdvert] = useState<SingleOfferDetailsRendererProps>();
    const [moreButtonActiveId, setMoreButtonActiveId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [offer, setOffer] = useState<TenderDetailsResponse>();
    const [notes, setNotes] = useState();

    const handleChangeReaction = async (name: keyof ReactionType, value: boolean, offer: TenderDetailsResponse) => {
        const userReaction = Object.assign({}, offer.reaction);

        if (name === 'likeStatus' && userReaction.unlikeStatus && value) {
            userReaction.unlikeStatus = false;
        } else if (name === 'unlikeStatus' && userReaction.likeStatus && value) {
            userReaction.likeStatus = false;
        }

        (userReaction[name] as boolean) = value;
        const city = offer.location;

        const response = await commonApiRequests.setOfferReaction(offer.tenderId, city, userReaction as UserReactions, TENDER_MODULE);
        setPreparedAdvert((prev) => ({
            ...prev,
            detailsCardData: {
                ...prev!.detailsCardData,
                interactiveElements: generateInteractiveElements({ ...offer, reaction: response })
            }
        }));
    };

    const handleSaveNote = async (value: string, advertId: number, type: string, id?: number) => {
        const response = await commonApiRequests.updateNote(type, value, advertId, id || 0);

        if (response) {
            if (id) {
                setPreparedAdvert((prev) => {
                    return {
                        ...prev,
                        detailsCardData: {
                            ...prev!.detailsCardData,
                            tabsContent: {
                                ...prev!.detailsCardData.tabsContent,
                                notesTabContent: {
                                    ...prev!.detailsCardData.tabsContent.notesTabContent!,
                                    notes: prev!.detailsCardData.tabsContent.notesTabContent!.notes!.map((x) => {
                                        if (x.noteId === id) {
                                            return {
                                                ...x,
                                                content: value
                                            };
                                        }

                                        return x;
                                    })
                                }
                            }
                        }
                    };
                });
            } else {
                setPreparedAdvert((prev) => {
                    return {
                        ...prev,
                        detailsCardData: {
                            ...prev!.detailsCardData,
                            tabsContent: {
                                ...prev!.detailsCardData.tabsContent,
                                notesTabContent: {
                                    ...prev!.detailsCardData.tabsContent.notesTabContent!,
                                    notes: prev!.detailsCardData.tabsContent.notesTabContent!.notes ? [response].concat(prev!.detailsCardData.tabsContent.notesTabContent!.notes as unknown as ExtendedNote) : [response]
                                },
                                numberOfNotes: (prev!.detailsCardData.tabsContent.numberOfNotes ?? 0) + 1
                            }
                        }
                    };
                });
            }
        }
    };

    const generateInteractiveElements = (offer: TenderDetailsResponse): InteractiveElements[] => {
        const offerLink = generateSingleOfferLink(offer.encryptedId, 'tender');

        const isMoreButtonActive = moreButtonActiveId === offer.tenderId.toString();
        const offerActionsElements = generateOfferActionsElements(offerLink, offer.link);

        return [
            {
                tooltipText: 'Wyświetlono',
                icon: IconEnum.EYE,
                active: offer.reaction?.viewedStatus,
                disabled: offer.reaction?.viewedStatus === undefined,
                onClick: () => handleChangeReaction('viewedStatus', !offer.reaction?.viewedStatus, offer)
            },
            {
                tooltipText: 'Lubię to',
                icon: IconEnum.LIKE,
                active: offer.reaction?.likeStatus,
                onClick: () => handleChangeReaction('likeStatus', !offer.reaction?.likeStatus, offer)
            },
            {
                tooltipText: 'Nie lubię tego',
                icon: IconEnum.DISLIKE,
                active: offer.reaction?.unlikeStatus,
                disabled: offer.reaction?.unlikeStatus === undefined,
                onClick: () => handleChangeReaction('unlikeStatus', !offer.reaction?.unlikeStatus, offer)
            },
            {
                tooltipText: 'Notatki',
                icon: IconEnum.MESSAGE,
                active: offer.notes && offer.notes?.length ? offer.notes?.length > 0 : false,
                counter: offer.notes?.length ? offer.notes?.length || 1 : undefined,
                openTabOnClick: TabType.NOTES
            },
            {
                tooltipText: 'Ogłoszenie nieaktualne',
                icon: IconEnum.BLOCKED,
                active: offer.reaction?.hideStatus,
                disabled: offer.reaction?.hideStatus === undefined,
                onClick: () => handleChangeReaction('hideStatus', !offer.reaction?.hideStatus, offer)
            },
            {
                tooltipText: 'Przeprowadzona rozmowa',
                icon: IconEnum.PHONE,
                active: offer.reaction?.phoneStatus,
                disabled: offer.reaction?.phoneStatus === undefined,
                onClick: () => handleChangeReaction('phoneStatus', !offer.reaction?.phoneStatus, offer)
            },
            {
                tooltipText: 'Umówione spotkanie',
                icon: IconEnum.PEOPLES_3,
                active: offer.reaction?.arrangedStatus,
                disabled: offer.reaction?.arrangedStatus === undefined,
                onClick: () => handleChangeReaction('arrangedStatus', !offer.reaction?.arrangedStatus, offer)
            },
            ...offer.photos && offer.photos.length > 0
                ? [
                    {
                        icon: IconEnum.PHOTOS,
                        isModalOpen: true,
                        isLink: true,
                        text: <p>Zobacz zdjęcia</p>,
                        isPhotoBtn: true,
                        visibleDuringModal: true,
                        sliderElements: {
                            photos: createImagesForSlider(offer.photos, TENDER_MODULE) as SliderImages[],
                            title: offer.title
                        }
                    }
                ]
                : [],
            {
                icon: 'squares' as IconEnum,
                tooltipText: isTouchDevice() ? undefined : 'Więcej',
                onClick: () => isMoreButtonActive ? setMoreButtonActiveId(null) : setMoreButtonActiveId(offer.tenderId.toString()),
                additionalJSXSibling: isMoreButtonActive ? <OfferActions isInModal elements={offerActionsElements} /> : undefined
            }
        ];
    };

    const handleDelete = async (noteId: number, type: string) => {
        const response = await commonApiRequests.deleteNote(type, noteId);

        if (response) {
            setPreparedAdvert((prev) => {
                return {
                    ...prev,
                    detailsCardData: {
                        ...prev!.detailsCardData,
                        tabsContent: {
                            ...prev!.detailsCardData.tabsContent,
                            notesTabContent: {
                                ...prev!.detailsCardData.tabsContent.notesTabContent!,
                                notes: prev!.detailsCardData.tabsContent.notesTabContent!.notes!.filter((x) => x.noteId !== noteId)
                            },
                            numberOfNotes: prev!.detailsCardData.tabsContent.numberOfNotes! - 1
                        }
                    }
                };
            });
        }
    };

    useEffect(() => {
        setIsLoading(true);
        tenderOffersApiRequests.getTenderOfferByHashId(hash).then(async (offer) => {
            setOffer(offer);
            const notes = await commonApiRequests.getNotes(TENDER_MODULE, offer.tenderId.toString());

            setNotes(notes);
            setIsLoading(false);
        }).catch(() => {
            setPreparedAdvert(undefined);
            setIsLoading(false);
        });
    }, [hash]);

    useEffect(() => {
        if (offer) {
            const detailsTabContent = tenderOfferListFunctions.generateOfferDetailsTabContent({
                ...offer,
                detailedContent: { description: offer.description },
                module: TENDER_MODULE,
                numberOfNotes: offer.notes ? offer.notes.length : 0,
                portal: offer.portalName
            });

            const detailsCardData = {
                id: offer.tenderId,
                interactiveElements: generateInteractiveElements(offer),
                additionalInfoBadge: offer.dateAdded,
                thumbnailPath: offer.photos && offer.photos.length > 0 ? convertToThumbnailPath(offer.photos[0], TENDER_MODULE) : './../defaultImg.png',
                tabsContent: {
                    interactiveElements: generateInteractiveElements(offer),
                    detailsTabContent: { ...detailsTabContent, adContext: { adId: offer.tenderId, adModule: TENDER_MODULE } },
                    notesTabContent: {
                        notes: notes,
                        handleDelete: (noteId: string) => handleDelete(Number(noteId), TENDER_MODULE),
                        handleSaveNote: (value: string, id?: string) => handleSaveNote(value, offer.tenderId, TENDER_MODULE, Number(id))
                    },
                    numberOfNotes: offer.notes && offer.notes.length > 0 ? offer.notes.length : 0,
                    handleLoadNotes: () => {
                    }
                }
            };

            setPreparedAdvert({ detailsCardData });
        }
    }, [
        offer,
        moreButtonActiveId,
        notes
    ]);

    return (
        <Wrapper>
            {isLoading
                ? <Indicator/>
                : preparedAdvert?.detailsCardData
                    ? <SingleOfferDetailsRenderer detailsCardData={preparedAdvert.detailsCardData}/>
                    : <NoAdvertContainer>Nie znaleziono ogłoszenia o podanym id.</NoAdvertContainer>}
        </Wrapper>
    );
};

export default SingleTenderDetailPage;
